import { useState } from 'react';
import { CameraOff, Maximize2, Minimize2, Video } from 'lucide-react';

import { cn } from '../../lib/utils';
import { CameraFeed } from './camera-feed';

export type CameraGridItem = {
  id: string;
  name: string;
};

export function CameraGrid({
  cameras,
  selectedIds,
  token,
}: {
  cameras: CameraGridItem[];
  selectedIds: string[];
  token?: string | null;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const allSelected = cameras.filter((camera) => selectedIds.includes(camera.id));

  // Auto-clear the expanded tile if that camera was deselected.
  const expanded =
    expandedId && allSelected.some((c) => c.id === expandedId) ? expandedId : null;
  const visible = expanded ? allSelected.filter((c) => c.id === expanded) : allSelected;

  if (cameras.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-muted">
          <CameraOff className="h-12 w-12" />
          <div className="text-base font-semibold text-foreground">No cameras available</div>
        </div>
      </div>
    );
  }

  if (allSelected.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-muted">
          <Video className="h-12 w-12" />
          <div className="text-base font-semibold text-foreground">No cameras selected</div>
        </div>
      </div>
    );
  }

  // Only show the expand button when there's more than one tile to choose
  // between; it has no purpose with a single selected camera.
  const canExpand = allSelected.length > 1;
  const isSingleLayout = visible.length === 1;

  return (
    <div
      className={cn(
        // `minmax(260px, 1fr)` rows give BOTH behaviors at once:
        //   - admin pages where the parent has no height: rows are 260px
        //     each (the floor) and the grid sizes itself by its tiles;
        //   - kiosk dashboard where the parent has a real height: rows
        //     stretch to 1fr each and the grid fills the available space.
        // h-full lets the kiosk path actually expand (the kiosk parent is
        // a flex column with explicit height); on admin h-full resolves
        // to auto and the floor takes over.
        'grid h-full gap-4',
        isSingleLayout
          ? // The single row needs its own floor, above the multi-tile floor.
            // Without it `grid-rows-1` (minmax(0,1fr)) collapses to content
            // height on admin, so "expand" made the stream smaller, not bigger.
            // The vh term keeps the floor inside short viewports.
            'grid-cols-1 grid-rows-[minmax(min(70vh,560px),1fr)]'
          : 'grid-cols-1 xl:grid-cols-2 auto-rows-[minmax(260px,1fr)] xl:auto-rows-[minmax(300px,1fr)]',
      )}
    >
      {visible.map((camera, index) => {
        const oddLast =
          !isSingleLayout &&
          visible.length > 1 &&
          visible.length % 2 === 1 &&
          index === visible.length - 1;
        const isExpanded = expanded === camera.id;
        return (
          <div
            key={camera.id}
            className={cn(
              'verne-pop-in flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-shell',
              oddLast && 'xl:col-span-2',
            )}
          >
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-2.5">
              <div className="text-sm font-semibold text-foreground">{camera.name}</div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="rounded-full bg-success/15 px-2.5 py-0.5 text-[11px] font-semibold text-success">
                  Live
                </div>
                {canExpand && (
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : camera.id)}
                    className="rounded-md p-1 text-muted transition-colors duration-150 hover:bg-surface-raised hover:text-foreground active:scale-95"
                    aria-label={isExpanded ? 'Collapse to grid' : 'Expand to full viewer'}
                  >
                    {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                  </button>
                )}
              </div>
            </div>
            {/* Stream area fills remaining tile height; absolute-positioned img
                lets the feed fit-to-container without forcing a min height. */}
            <div className="relative min-h-0 flex-1 bg-surface-muted">
              <CameraFeed
                cameraId={camera.id}
                token={token}
                alt={camera.name}
                className={cn(
                  'absolute inset-0 h-full w-full',
                  // The center camera is an overview shot — fit the whole
                  // frame inside the tile (with letterbox bars) instead of
                  // cropping. Other feeds keep cover-fit so faces/grippers
                  // fill the tile.
                  camera.id === 'third_person' ? 'object-contain' : 'object-cover',
                )}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
